import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import AdminLogin from "@/components/admin/AdminLogin";
import AnalyticsDashboard from "@/components/admin/AnalyticsDashboard";
import AdminAnalytics from "@/components/admin/analytics/AdminAnalytics";
import TherapistVerificationsTable from "@/components/admin/TherapistVerificationsTable";
import { Button } from "@/components/ui/button";
import { LogOut, MessageCircle, Loader2, BarChart3, Bug, UserCheck } from "lucide-react";
import { Toaster } from "sonner";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AnalyticsDebugPanel from "@/components/admin/analytics/AnalyticsDebugPanel";
const Admin: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [feedbackCount, setFeedbackCount] = useState<number | null>(null);
  const navigate = useNavigate();
  useEffect(() => {
    const checkAdminStatus = async () => {
      setIsCheckingAuth(true);
      try {
        console.log("Checking admin status...");

        // Check if using hardcoded admin
        const isHardcodedAdmin = localStorage.getItem('isAdmin') === 'true';
        if (isHardcodedAdmin) {
          console.log("Found admin flag in localStorage");

          // Still verify against session to ensure user is logged in
          const {
            data: {
              session
            }
          } = await supabase.auth.getSession();
          if (!session) {
            console.log("No active session found despite admin flag");
            localStorage.removeItem('isAdmin');
            setIsAuthenticated(false);
            setIsCheckingAuth(false);
            return;
          }

          // Double check admin status from database for added security
          const {
            data: hasAdminRole,
            error: roleError
          } = await supabase.rpc('has_role', {
            role_param: 'admin'
          });
          if (!roleError && hasAdminRole) {
            console.log("Admin role confirmed via database");
            setIsAuthenticated(true);
            setIsCheckingAuth(false);
            return;
          }

          // Special case for lou@schillaci.me (backwards compatibility)
          if (session.user.email === "lou@schillaci.me") {
            console.log("Allowing admin access for lou@schillaci.me");
            setIsAuthenticated(true);
            setIsCheckingAuth(false);
            return;
          }
          console.log("Admin flag in localStorage but no admin role in database");
          // Only remove admin flag if not lou@schillaci.me
          if (session.user.email !== "lou@schillaci.me") {
            localStorage.removeItem('isAdmin');
          }
        }

        // Check if user is logged in
        const {
          data: {
            session
          }
        } = await supabase.auth.getSession();
        if (!session) {
          console.log("No active session found");
          setIsAuthenticated(false);
          setIsCheckingAuth(false);
          return;
        }
        console.log("Session found, checking if user has admin role");

        // Special case for lou@schillaci.me (backwards compatibility)
        if (session.user.email === "lou@schillaci.me") {
          console.log("Special case for lou@schillaci.me, granting admin access");
          localStorage.setItem('isAdmin', 'true');
          localStorage.setItem('adminEmail', session.user.email);
          setIsAuthenticated(true);
          setIsCheckingAuth(false);
          return;
        }

        // Check if user has admin role using the has_role function
        const {
          data: hasAdminRole,
          error: roleError
        } = await supabase.rpc('has_role', {
          role_param: 'admin'
        });
        if (roleError) {
          console.error("Error checking admin role:", roleError);
          toast.error("Error verifying admin status");
          setIsAuthenticated(false);
          return;
        }
        console.log("Admin role check result:", hasAdminRole);
        if (hasAdminRole) {
          localStorage.setItem('isAdmin', 'true');
          localStorage.setItem('adminEmail', session.user.email || '');
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Error checking admin status:", error);
        setIsAuthenticated(false);
      } finally {
        setIsCheckingAuth(false);
      }
    };
    checkAdminStatus();

    // Set up auth state listener
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("Auth state changed:", event);
      if (event === 'SIGNED_OUT') {
        console.log("User signed out, updating auth state");
        localStorage.removeItem('isAdmin');
        localStorage.removeItem('adminEmail');
        setIsAuthenticated(false);
      } else if (event === 'SIGNED_IN') {
        console.log("User signed in, checking admin status");
        checkAdminStatus();
      }
    });

    // Fetch feedback count
    const fetchFeedbackCount = async () => {
      try {
        const { data, error } = await supabase.rpc('get_all_feedback');
        if (error) throw error;
        setFeedbackCount(Array.isArray(data) ? data.length : 0);
      } catch (err) {
        setFeedbackCount(null);
      }
    };
    fetchFeedbackCount();

    return () => {
      subscription.unsubscribe();
    };
  }, []);
  const handleLogout = async () => {
    console.log("Logging out admin user");
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('adminEmail');
    await supabase.auth.signOut();
    setIsAuthenticated(false);
    toast.success("Logged out successfully");
    navigate('/auth');
  };
  const handleLoginSuccess = () => {
    console.log("Login successful, setting isAuthenticated to true");
    setIsAuthenticated(true);
  };
  if (isCheckingAuth) {
    return <div className="min-h-screen flex justify-center items-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-[#3BAFDA]" />
          <p className="text-lg text-[#5A636E]">Checking authentication...</p>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-gradient-to-b from-[#E8F0F8] to-white">
      <Toaster position="top-right" />
      <div className="container mx-auto py-8 px-4 max-w-full">
        <header className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-[#333B44] flex items-center gap-2">
              <img src="/clou_logo.png" alt="Clou Logo" className="h-8 sm:h-10 w-auto object-contain" />
              Admin Dashboard
            </h1>
            <span className="text-xs text-[#5A636E]">V1.0.001</span>
          </div>
          {isAuthenticated && <div className="flex flex-row items-center gap-3">
              <Button variant="outline" size="lg" className="flex items-center gap-2 border-[#CCCCCC] hover:bg-[#E8F0F8] font-medium rounded-lg px-6 py-2" onClick={handleLogout} type="button">
                <LogOut className="h-5 w-5" />
                Logout
              </Button>
            </div>}
        </header>

        {isAuthenticated ? <>
            <Tabs defaultValue="overview" className="space-y-4">
              <TabsList>
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="analytics">
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Analytics
                </TabsTrigger>
                <TabsTrigger value="verifications">
                  <UserCheck className="h-4 w-4 mr-2" />
                  Verifications
                </TabsTrigger>
              </TabsList>
              <TabsContent value="overview">
                <AnalyticsDashboard feedbackCount={feedbackCount} />
              </TabsContent>
              <TabsContent value="analytics">
                <AdminAnalytics />
                <AnalyticsDebugPanel />
              </TabsContent>
              <TabsContent value="verifications">
                <TherapistVerificationsTable />
              </TabsContent>
            </Tabs>
          </> : <AdminLogin onLoginSuccess={handleLoginSuccess} />}
      </div>
    </div>;
};
export default Admin;